import Cookies from "js-cookie";
import {
	addCategory,
	deleteCategory,
	fetchCategories,
	fetchLinks,
	fetchProfiles,
	updateCategory,
	uploadDefaultImage,
	uploadNewImage
} from "./firebaseTools";

const isImageValid = (image) => {
	return SUPPORTED_IMAGE_FORMATS.includes(image.type) ||
		(typeof image === 'string' && image.startsWith('https://firebasestorage.googleapis.com'));
}

const sortLinks = (links) => {
	return links.sort((a, b) => a.name.localeCompare(b.name));
};

export const MAX_CATEGORIES = 5;
export const SUPPORTED_IMAGE_FORMATS = ['image/jpeg', 'image/png'];
export const FOOTER_URL_LINK = "https://portfolio.luctatu.fr/";
export const FOOTER_TITLE = "Actibus immensis urbs fulget Massiliensis";
export const PAGE_FULL_TITLE = "My Work Space";
export const PAGE_SHORT_TITLE = "MWS";
export const LEGACY_ALL_PROFILE_ID = "__ALL__";
export const VIRTUAL_ALL_PROFILE_ID = "all_profiles";
export const ALL_PROFILE = {id: VIRTUAL_ALL_PROFILE_ID, profileName: "ALL", isVirtual: true};

const normalizeProfileId = (profileId) => (
	profileId === LEGACY_ALL_PROFILE_ID ? VIRTUAL_ALL_PROFILE_ID : profileId
);

const normalizeProfileIds = (profileIds = []) => (
	Array.from(new Set((profileIds ?? []).map(normalizeProfileId).filter(Boolean)))
);

const toFiniteNumber = (value, fallback = 0) => {
	const numeric = Number(value);
	return Number.isFinite(numeric) ? numeric : fallback;
};

export const normalizeProfile = (profile) => {
	if (!profile) return null;
	return {
		id: normalizeProfileId(profile.id),
		profileName: String(profile.profileName ?? "").toUpperCase(),
		isVirtual: Boolean(profile.isVirtual),
	};
};

export const normalizeLink = (link) => {
	const profileIds = Array.isArray(link.profileIds)
		? link.profileIds
		: (link.profileId ? [link.profileId] : []);

	return {
		...link,
		profileIds: normalizeProfileIds(profileIds),
	};
};

export const normalizeCategory = (category, fallbackProfileId = null) => {
	const profileIds = normalizeProfileIds(
		Array.isArray(category.profileIds)
			? category.profileIds
			: (category.profileId ? [category.profileId] : [])
	);
	const idxPerProfile = {};

	if (category.idxPerProfile && typeof category.idxPerProfile === "object") {
		Object.entries(category.idxPerProfile).forEach(([profileId, idx]) => {
			const normalizedProfileId = normalizeProfileId(profileId);
			if (!normalizedProfileId) return;
			idxPerProfile[normalizedProfileId] = toFiniteNumber(idx);
		});
	}

	// Legacy compatibility for old schema using `idx`.
	const normalizedFallbackProfileId = normalizeProfileId(fallbackProfileId);
	if (category.idx != null && normalizedFallbackProfileId && idxPerProfile[normalizedFallbackProfileId] == null) {
		idxPerProfile[normalizedFallbackProfileId] = toFiniteNumber(category.idx);
	}

	return {
		...category,
		profileIds,
		idxPerProfile,
	};
};

export const isAllProfile = (profile) => normalizeProfileId(profile?.id) === VIRTUAL_ALL_PROFILE_ID;

export const includesProfile = (entity, profileId) => {
	if (!profileId) return false;
	const normalizedProfileId = normalizeProfileId(profileId);
	return normalizeProfileIds(entity?.profileIds).includes(normalizedProfileId);
};

export const getCategoryIdxForProfile = (category, profileId) => {
	if (!category) return 0;
	const normalizedProfileId = normalizeProfileId(profileId);
	if (isAllProfile({id: normalizedProfileId})) {
		if (category.idx != null) {
			return toFiniteNumber(category.idx);
		}
		if (category.idxPerProfile?.[VIRTUAL_ALL_PROFILE_ID] != null) {
			return toFiniteNumber(category.idxPerProfile[VIRTUAL_ALL_PROFILE_ID]);
		}
		if (category.idxPerProfile?.[LEGACY_ALL_PROFILE_ID] != null) {
			return toFiniteNumber(category.idxPerProfile[LEGACY_ALL_PROFILE_ID]);
		}
		const idxValues = Object.values(category.idxPerProfile ?? {}).map((idx) => toFiniteNumber(idx)).filter(Number.isFinite);
		return idxValues.length ? Math.min(...idxValues) : toFiniteNumber(category.idx);
	}

	if (category.idxPerProfile?.[normalizedProfileId] != null) {
		return toFiniteNumber(category.idxPerProfile[normalizedProfileId]);
	}
	if (profileId === LEGACY_ALL_PROFILE_ID && category.idxPerProfile?.[LEGACY_ALL_PROFILE_ID] != null) {
		return toFiniteNumber(category.idxPerProfile[LEGACY_ALL_PROFILE_ID]);
	}
	return toFiniteNumber(category.idx);
};

export const sortProfiles = (profiles) => {
	const virtualAll = profiles.find((profile) => profile.id === VIRTUAL_ALL_PROFILE_ID);
	const rest = profiles
		.filter((profile) => profile.id !== VIRTUAL_ALL_PROFILE_ID)
		.filter((profile) => profile && profile.profileName)
		.sort((a, b) => a.profileName.localeCompare(b.profileName));

	return virtualAll ? [virtualAll, ...rest] : rest;
};

export const calculateNextIdxForProfile = (categories = [], profileId) => {
	const normalizedProfileId = normalizeProfileId(profileId);
	if (!normalizedProfileId) return 0;
	const values = categories
		.map((category) => getCategoryIdxForProfile(category, normalizedProfileId))
		.filter(Number.isFinite);
	return values.length ? Math.max(...values) + 1 : 0;
};

export const expandProfileIdsForSelection = (selectedProfileIds, profiles) => {
	const normalizedSelection = normalizeProfileIds(selectedProfileIds ?? []);
	const allRealProfileIds = normalizeProfileIds(
		(profiles ?? []).map((profile) => profile?.id)
	).filter((profileId) => profileId !== VIRTUAL_ALL_PROFILE_ID);

	if (normalizedSelection.includes(VIRTUAL_ALL_PROFILE_ID)) {
		return allRealProfileIds;
	}

	return normalizedSelection.filter((profileId) => profileId !== VIRTUAL_ALL_PROFILE_ID);
};

export const getFormattedValue = (value) => {
	return value
		.split(' ')
		.map(word => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');
};

export const padCategories = (categories) => {
	const categoriesToDisplay = [...categories];
	const totalCategories = categoriesToDisplay.length;
	const remainder = totalCategories % MAX_CATEGORIES;

	if (remainder !== 0) {
		const emptyCategoriesCount = MAX_CATEGORIES - remainder;
		for (let i = 0; i < emptyCategoriesCount; i++) {
			categoriesToDisplay.push({category: "", idx: 10000 + i});
		}
	}

	return categoriesToDisplay;
};

export const sortCategories = (categories) => {
	return categories
		.filter(category => category !== null)
		.sort((a, b) => a.idx - b.idx);
};

export const generateUniqueLinkName = (
	links,
	name,
	category,
	isMergeCategory = false,
	isMigration = false,
	profileId = null
) => {
	const existingLinks = links.filter(link =>
		link.category === category
		&& link.name.replace(/ \(\d+\)$/, '') === name
		&& (!profileId || includesProfile(normalizeLink(link), profileId))
	);

	if (!isMergeCategory) {
		if (isMigration) {
			return name;
		} else {
			return existingLinks.length > 0 ? `${name} (${existingLinks.length + 1})` : name;
		}
	} else {
		return existingLinks.length > 0 ? `${name} (${existingLinks.length + 1})` : name;
	}
};

export const validateFormAddLink = (formData) => {
	const {category, name, url, image, profileIds} = formData;
	if (!category || !name || !url) {
		alert('All fields are required.');
		return false;
	}
	if (!Array.isArray(profileIds) || profileIds.length === 0) {
		alert('At least one profile must be selected.');
		return false;
	}
	if (image && typeof image !== 'string' && !SUPPORTED_IMAGE_FORMATS.includes(image.type)) {
		alert('Unsupported image format. Please upload a JPEG or PNG.');
		return false;
	}
	return true;
};

export const getLinksByCategories = (categoriesToDisplay, links) => {
	return categoriesToDisplay.reduce((acc, categoryObj) => {
		const {category} = categoryObj;
		acc[category] = sortLinks(links.filter(link => link.category === category));
		return acc;
	}, {});
}

export const uploadImageToFirebase = async (image, addError) => {
	if (image) {
		if (!isImageValid(image)) {
			alert('Unsupported image format. Please upload a JPEG or PNG.');
		}

		if (typeof image !== 'string') {
			return await uploadNewImage(image, addError);
		} else {
			return image;
		}
	} else {
		return await uploadDefaultImage(addError);
	}
}

export let removeCategoryFromFirebase = async (categoryId, addError) => {
	if (categoryId === null) {
		addError('Invalid category ID for removal');
		return false;
	}

	return await deleteCategory(categoryId, addError);
};

export const getCategories = async (setCategories, addError, profile = ALL_PROFILE) => {
	const categories = await fetchCategories(addError);
	const normalized = (categories ?? []).map((category) =>
		normalizeCategory(category, profile?.id)
	);

	const filtered = isAllProfile(profile)
		? normalized
		: normalized.filter((category) => includesProfile(category, profile?.id));

	const withDisplayIdx = filtered.map((category) => ({
		...category,
		idx: getCategoryIdxForProfile(category, profile?.id ?? VIRTUAL_ALL_PROFILE_ID),
	}));

	setCategories(sortCategories(withDisplayIdx));
	return withDisplayIdx;
};

export const getLinks = async (setLinks, addError) => {
	const links = await fetchLinks(addError);
	const normalized = (links ?? []).map(normalizeLink);
	setLinks(normalized);
	return normalized;
}

export const filterLinksByProfile = (links, profile) => {
	if (!profile) return [];
	if (isAllProfile(profile)) return links;
	return links.filter((link) => includesProfile(normalizeLink(link), profile.id));
};

export const getProfiles = async (setProfiles, setProfile, addError) => {
	const result = await fetchProfiles(addError);
	const seenIds = new Set([VIRTUAL_ALL_PROFILE_ID]);
	const normalizedFetched = (result ?? [])
		.map(normalizeProfile)
		.filter((item) => item && item.id && item.profileName !== "ALL")
		.filter((item) => {
			if (seenIds.has(item.id)) return false;
			seenIds.add(item.id);
			return true;
		});
	const normalized = [ALL_PROFILE, ...normalizedFetched];
	const sorted = sortProfiles(normalized);

	setProfiles(sorted);
	const savedProfileId = Cookies.get("profileId");
	const fallback = sorted.find((profile) => profile.id === savedProfileId)
		?? sorted[0]
		?? ALL_PROFILE;

	setProfile(fallback);
	Cookies.set("profileId", fallback.id, {expires: 7, path: "/"});
	return sorted;
};

export const handleImageUpload = async (image, addError) => {
	if (image) {
		if (typeof image === 'string' && image.startsWith('https://')) {
			return image;
		}
		return await uploadNewImage(image, addError);
	}
	return await uploadDefaultImage(addError);
};

export const updateCategoryIndices = async (
	categories,
	categoryId,
	categoryIdx,
	oldIdx,
	addError,
	profileId,
	isRemoved = false
) => {
	try {
		if (!profileId) {
			addError('Missing profile ID while updating category indices');
			return categories;
		}

		const isAll = isAllProfile({id: profileId});
		const newIdx = Number(categoryIdx);
		const previousIdx = Number(oldIdx);
		let updatedCategories;

		if (isRemoved) {
			const removedCategory = categories.find(category => category.id === categoryId);
			if (!removedCategory) {
				addError(`Category with ID ${categoryId} not found.`);
				return categories;
			}

			if (isAll) {
				const removedIdx = toFiniteNumber(removedCategory.idx);
				updatedCategories = categories
					.filter(category => category.id !== categoryId)
					.map(category => ({
						...category,
						idx: toFiniteNumber(category.idx) > removedIdx
							? toFiniteNumber(category.idx) - 1
							: toFiniteNumber(category.idx)
					}));
			} else {
				updatedCategories = categories
					.filter(category => category.id !== categoryId)
					.map(category => ({
						...category,
						idxPerProfile: {
							...(category.idxPerProfile ?? {}),
							[profileId]: getCategoryIdxForProfile(category, profileId)
								> getCategoryIdxForProfile(removedCategory, profileId)
								? getCategoryIdxForProfile(category, profileId) - 1
								: getCategoryIdxForProfile(category, profileId)
						}
					}));
			}
		} else {
			updatedCategories = categories.map(category => {
				const currentIdx = isAll
					? toFiniteNumber(category.idx)
					: getCategoryIdxForProfile(category, profileId);
				if (category.id === categoryId) {
					if (isAll) {
						return {
							...category,
							idx: newIdx
						};
					}
					return {
						...category,
						idxPerProfile: {
							...(category.idxPerProfile ?? {}),
							[profileId]: newIdx
						}
					};
				}
				if (previousIdx > newIdx && currentIdx >= newIdx && currentIdx < previousIdx) {
					if (isAll) {
						return {...category, idx: currentIdx + 1};
					}
					return {
						...category,
						idxPerProfile: {
							...(category.idxPerProfile ?? {}),
							[profileId]: currentIdx + 1
						}
					};
				}
				if (previousIdx < newIdx && currentIdx > previousIdx && currentIdx <= newIdx) {
					if (isAll) {
						return {...category, idx: currentIdx - 1};
					}
					return {
						...category,
						idxPerProfile: {
							...(category.idxPerProfile ?? {}),
							[profileId]: currentIdx - 1
						}
					};
				}
				return category;
			});
		}

		await Promise.all(updatedCategories.map(category =>
			updateCategory(category, addError)
		));

		return updatedCategories;
	} catch (error) {
		addError(`Error updating categories: ${error.message}`);
		return categories;
	}
};

export const reindexCategoriesForProfiles = async (
	categories = [],
	profileIds = [],
	addError
) => {
	try {
		const normalizedProfileIds = Array.from(new Set(
			(profileIds ?? [])
				.map(normalizeProfileId)
				.filter((profileId) => profileId && profileId !== VIRTUAL_ALL_PROFILE_ID)
		));

		if (normalizedProfileIds.length === 0) {
			return categories;
		}

		const nextCategories = categories.map((category) => ({
			...category,
			idxPerProfile: {...(category.idxPerProfile ?? {})}
		}));

		normalizedProfileIds.forEach((profileId) => {
			const sortedForProfile = nextCategories
				.filter((category) => includesProfile(category, profileId))
				.sort((a, b) => getCategoryIdxForProfile(a, profileId) - getCategoryIdxForProfile(b, profileId));

			sortedForProfile.forEach((category, idx) => {
				category.idxPerProfile[profileId] = idx;
			});
		});

		nextCategories.forEach((category) => {
			const idxValues = Object.values(category.idxPerProfile ?? {})
				.map((value) => Number(value))
				.filter(Number.isFinite);
			if (idxValues.length > 0) {
				category.idx = Math.min(...idxValues);
			}
		});

		const writes = nextCategories.filter((category, index) => {
			const previous = categories[index];
			if (!previous || previous.id !== category.id) return true;
			if (Number(previous.idx) !== Number(category.idx)) return true;

			const prevEntries = Object.entries(previous.idxPerProfile ?? {});
			const nextEntries = Object.entries(category.idxPerProfile ?? {});
			if (prevEntries.length !== nextEntries.length) return true;

			return nextEntries.some(([profileId, idx]) => Number(previous.idxPerProfile?.[profileId]) !== Number(idx));
		});

		await Promise.all(writes.map((category) => updateCategory(category, addError)));
		return nextCategories;
	} catch (error) {
		addError(`Error while reindexing categories by profile: ${error.message}`);
		return categories;
	}
};

export const getUniqueImagesFromLinks = (links) => {
	const validUrls = (links ?? [])
		.map(link => link.image)
		.filter(url => url && typeof url === 'string' && (url.startsWith('https://') || url.startsWith('http://')));
	
	const seen = new Map();
	validUrls.forEach(url => {
		const baseUrl = url.split('?')[0];
		if (!seen.has(baseUrl)) {
			seen.set(baseUrl, url);
		}
	});
	
	return Array.from(seen.values());
};

export const verifyParamsUpdateLink = async (link, addError) => {
	if (!link?.id) {
		addError("Invalid link ID for update");
		return null;
	}

	const downloadUrl = await uploadImageToFirebase(link.image, addError);

	if (!downloadUrl || downloadUrl.empty) {
		addError("Download URL is empty");
		return null;
	}

	return downloadUrl;
};

export const ensureCategoryHasProfiles = async (
	category,
	profileIds,
	allCategories,
	setAllCategories,
	addError
) => {
	const existingCategory = allCategories.find((item) => item.id === category.id);
	if (!existingCategory) return category;

	const mergedProfileIds = Array.from(new Set([
		...(existingCategory.profileIds ?? []),
		...profileIds
	])).filter((id) => id && id !== VIRTUAL_ALL_PROFILE_ID);

	const updated = {
		...existingCategory,
		profileIds: mergedProfileIds,
		idxPerProfile: {...(existingCategory.idxPerProfile ?? {})}
	};

	let changed = false;
	profileIds.forEach((profileId) => {
		if (updated.idxPerProfile[profileId] == null) {
			updated.idxPerProfile[profileId] = calculateNextIdxForProfile(allCategories, profileId);
			changed = true;
		}
	});

	if (changed || mergedProfileIds.length !== (existingCategory.profileIds ?? []).length) {
		await updateCategory(updated, addError);
		setAllCategories((prev) => prev.map((item) => item.id === updated.id ? updated : item));
	}

	return updated;
};

export const handleAddCategory = async ({
	allCategories,
	setAllCategories,
	categories = [],
	setCategories,
	category,
	profileIds,
	addError
}) => {
	const normalizedProfileIds = normalizeProfileIds(profileIds ?? [])
		.filter((profileId) => profileId !== VIRTUAL_ALL_PROFILE_ID);

	const existing = allCategories.find((item) => item.category === category);
	if (existing) {
		const ensured = await ensureCategoryHasProfiles(
			existing,
			normalizedProfileIds,
			allCategories,
			setAllCategories,
			addError
		);
		if (setCategories) {
			setCategories((prev) => {
				const has = prev.some((item) => item.id === ensured.id);
				return has ? prev : [...prev, ensured];
			});
		}
		return ensured.id;
	}

	const idxPerProfile = {};
	normalizedProfileIds.forEach((profileId) => {
		idxPerProfile[profileId] = toFiniteNumber(calculateNextIdxForProfile(categories, profileId));
	});

	const newCategory = normalizeCategory({
		category,
		profileIds: normalizedProfileIds,
		idxPerProfile,
		idx: normalizedProfileIds.length
			? toFiniteNumber(Math.min(...Object.values(idxPerProfile)))
			: 0
	});

	const response = await addCategory(newCategory, addError);
	if (!response?.id) {
		return null;
	}

	const newCategoryWithId = {...newCategory, id: response.id};
	setAllCategories((prevCategories) => [...prevCategories, newCategoryWithId]);
	if (setCategories) {
		setCategories((prevCategories) => [...prevCategories, newCategoryWithId]);
	}
	return response.id;
};
